import { useState } from "react"
import ReactDOM from "react-dom"
import PropTypes from "prop-types"
import { DynamicDataView, DropdownMenu, StatusMessage, ComboBox } from "components/ui"
import { useToast } from "components/ui/Toast"
import { Button, Dropdown, StackView } from "@planningcenter/tapestry-react"
import AreaAttendeeDropdown from "./AreaAttendeeDropdown"
import { pluralize, get, capitalize } from "utils/helpers"
import { api } from "utils/api"
import { isAreaAttendeeDisabled } from "./areaRestriction"
import sortBy from "utils/sorter"
import Icon from "components/planning_center_icon"
import CheckInsIntegration from "../AttendanceDropdown/CheckInsIntegration"
import CheckIns from "../AttendanceDropdown/CheckIns"
import { confirmableAssignmentUpdate } from "./confirmableAssignmentUpdate"
import { navigate } from "@reach/router"

const attendeesByType = (attendeeType, assignedAttendees, unassignedAttendees) => {
  let attendees = []

  if (attendeeType === "assigned") {
    attendees = assignedAttendees
  } else if (attendeeType === "unassigned") {
    attendees = unassignedAttendees
  } else {
    attendees = [...assignedAttendees, ...unassignedAttendees]
  }

  return attendees
}

const Attendees = ({
  event,
  attendeeType,
  assignmentTypePaths,
  areas,
  assignedAttendees,
  unassignedAttendees,
  onAutoAssign,
  onAttendeeUnassign,
  onUnassignAllAlert,
  onAttendeeAssign,
  checkinsUrl,
  apiBaseUrl,
  checkInsBaseUrl,
  assignmentTypes,
  signupFeatures,
  subscribedToCheckIns,
  checkInsSettingsPath
}) => {
  const [sorter, setSorter] = useState({ sortBy: "firstName", sortDir: "asc" })
  const [selectedAttendeeId, setSelectedAttendeeId] = useState(null)
  const [assignmentAreaId, setAssignmentAreaId] = useState(null)
  const [statusMessage, setStatusMessage] = useState(null)
  const [statusRowMessage, setStatusRowMessage] = useState(null)

  const attendees = attendeesByType(attendeeType, assignedAttendees, unassignedAttendees)

  const [sessions] = useState([])
  const signupIntegrationId = event.signupIntegration?.integration_id

  const { addToast } = useToast()

  const setErrorStatus = (id, message = "Changes not saved") =>
    setStatusRowMessage({
      id,
      text: message,
      icon: "general#exclamation-triangle",
      type: "error"
    })

  const setSuccessStatus = id =>
    setStatusRowMessage({
      id,
      text: "Saved!",
      icon: "general#check",
      type: "success"
    })

  const getAreaItems = attendee => {
    return sortBy("position", Object.values(areas)).map(area => {
      if (!attendee) attendee = attendees.find(a => a.id === selectedAttendeeId)
      const isDisabled = isAreaAttendeeDisabled(area, attendee)

      return {
        ...area,
        isDisabled,
        // eslint-disable-next-line react/display-name
        render: menuItem => <AreaAttendeeDropdown menuItem={menuItem} attendee={attendee} />
      }
    })
  }

  const handleSort = ({ sortBy, sortDir }) => {
    setSorter({ sortBy, sortDir })
    return Promise.resolve()
  }

  const handleRowClick = row => {
    if (row.id === selectedAttendeeId) {
      return
    }

    setSelectedAttendeeId(row.id)
    setAssignmentAreaId(null)
    setStatusRowMessage(null)
  }

  const handleCancel = e => {
    if (e) e.stopPropagation()
    setSelectedAttendeeId(null)
    setAssignmentAreaId(null)
    setStatusRowMessage(null)
  }

  const handleAttendeeSave = e => {
    if (e) e.stopPropagation()
    const attendee = attendees.find(attendee => Number(attendee.id) === Number(selectedAttendeeId))

    setStatusRowMessage(
      { id: attendee.id, text: "Saving...", icon: "spinner" },
      saveAssignment(attendee)
    )
  }

  const handleViewRegistration = (attendee, e) => {
    if (e) e.stopPropagation()
    navigate(attendee.registrationPath)
  }

  const saveAssignment = attendee => {
    const areaId = Number(assignmentAreaId)
    const area = areas[areaId]

    if (areaId === 0 && attendee.assignedArea) {
      onAttendeeUnassign([attendee], areas[attendee.assignedArea.id])
        .then(_resp => setSuccessStatus(attendee.id))
        .catch(_error => setErrorStatus(attendee.id))
    } else if ((area && !attendee.assignedArea) || (area && attendee.assignedArea.id !== area.id)) {
      confirmableAssignmentUpdate({
        area,
        attendee,
        onConfirm: () => {
          onAttendeeAssign(attendee, area)
            .then(_resp => setSuccessStatus(attendee.id))
            .catch(error => setErrorStatus(attendee.id, error.message))
        },
        onCancel: () => {
          setStatusRowMessage(null)
          handleCancel()
        }
      })
    }

    setTimeout(() => setStatusRowMessage(null), 2500)
    handleCancel()
  }

  const handleUnassign = (attendee, e) => {
    e.stopPropagation()
    setStatusRowMessage(
      { id: attendee.id, text: "Saving...", icon: "spinner" },
      onAttendeeUnassign([attendee], areas[attendee.assignedArea.id])
        .then(_resp => setSuccessStatus(attendee.id))
        .catch(_error => setErrorStatus(attendee.id))
        .finally(() => setTimeout(() => setStatusRowMessage(null), 2500))
    )
  }

  const handleUnassignAll = attendees =>
    onUnassignAllAlert(() => handleButtonClick(() => onAttendeeUnassign(attendees)))

  const handleNewChange = selectedItem => {
    if (selectedItem) {
      return setAssignmentAreaId(selectedItem.id)
    } else {
      return handleNewChange({ id: 0, name: "None" })
    }
  }

  const handleAutoAssign = () => {
    let swalContent = document.createElement("div")
    ReactDOM.render(
      <>
        <p>
          When auto-assigning, we&apos;ll honor your gender, age/grade, and attendee type settings
          for each area. After auto-assign is complete, you can always make manual adjustments as
          needed.
        </p>
        <p>
          If your attendees are eligible for multiple areas, there are two ways we can distribute
          them:
        </p>
        <p>
          <strong>Waterfall</strong>: Fill area to capacity before using the next
        </p>
        <p>
          <strong>Balanced</strong>: Balance all areas evenly.
        </p>
      </>,
      swalContent
    )

    return alertModal(
      {
        title: "Ready to auto-assign?",
        type: "warning",
        content: swalContent,
        closeOnConfirm: true,
        buttons: {
          cancel: {
            text: "Nevermind",
            value: null,
            visible: true,
            closeModal: true
          },
          waterfall: {
            text: "Waterfall",
            value: "greedy",
            visible: true,
            closeModal: true,
            className: "swal-button--confirm"
          },
          balanced: {
            text: "Balanced",
            value: "balanced",
            visible: true,
            className: "swal-button--confirm ml-1",
            closeModal: true
          }
        }
      },
      button => handleButtonClick(() => onAutoAssign(button))
    )
  }

  const handleButtonClick = onButtonClick => {
    setStatusMessage({
      text: "Saving...",
      icon: "spinner"
    })

    return onButtonClick()
      .then(_r =>
        setStatusMessage({
          text: "Saved!",
          icon: "general#check",
          type: "success"
        })
      )
      .catch(_r =>
        setStatusMessage({
          text: "Changes not saved",
          icon: "general#exclamation-triangle",
          type: "error"
        })
      )
      .finally(_r => setTimeout(() => setStatusMessage(null), 1000))
  }

  const renderLegacyCheckInButton = item => {
    return (
      <div key={item.text}>
        <form action={`${checkinsUrl}${assignmentTypePaths.checkinsCreate}`} method="post">
          <input type="hidden" name="authenticity_token" value={$.rails.csrfToken()} />
          <Button
            variant="naked"
            onClick={e => e.target.parentElement.submit()}
            distribution="flex-start"
            width="100%"
          >
            <span
              css={{
                color: "var(--t-fill-color-product-checkins-050)",
                position: "relative",
                top: 2,
                fontSize: "1rem",
                marginRight: 4
              }}
            >
              <Icon symbol="logomark#check-ins" />
            </span>
            Check In
          </Button>
        </form>
      </div>
    )
  }

  const renderCheckInButton = () => {
    return (
      <>
        {signupIntegrationId ? (
          <CheckInsIntegration sessions={sessions} checkInsSettingsPath={checkInsSettingsPath} />
        ) : (
          <CheckIns
            apiBaseUrl={apiBaseUrl}
            checkInsBaseUrl={checkInsBaseUrl}
            event={event}
            assignmentTypes={assignmentTypes}
            checkInsSettingsPath={checkInsSettingsPath}
          />
        )}
      </>
    )
  }

  const renderAreaColumn = (row, column) => {
    if (selectedAttendeeId !== row.id) {
      return (
        <div className="d-f ai-c" css={{ width: 265 }}>
          <div>{get(row, column.cell, "")}</div>
          {row.assignedArea ? (
            <Button
              as="button"
              theme="destroy"
              icon={{ name: "general.outlinedMinusCircle" }}
              tooltip={{ title: "Unassign" }}
              variant="naked"
              size="md"
              className="ml-1"
              onClick={e => handleUnassign(row, e)}
            />
          ) : null}
          {statusRowMessage && statusRowMessage.id === row.id && (
            <StatusMessage {...statusRowMessage} style={{ marginLeft: "0.5rem", paddingTop: 1 }} />
          )}
        </div>
      )
    }

    return (
      <div className="d-f ai-c" css={{ marginLeft: -8, width: 265 }}>
        {selectedAttendeeId && (
          <ComboBox
            placeholder="None"
            items={sortBy("isDisabled", getAreaItems(), "desc")}
            itemKey="name"
            onSelect={handleNewChange}
            defaultItem={row.assignedArea || null}
          />
        )}
        <Button
          marginHorizontal={1}
          size="xs"
          icon={{ name: "general.check" }}
          tooltip={{ title: "Save" }}
          theme="create"
          onClick={handleAttendeeSave}
          disabled={assignmentAreaId !== 0 && !assignmentAreaId}
        />
        <Button
          size="xs"
          icon={{ name: "general.x" }}
          tooltip={{ title: "Cancel" }}
          onClick={handleCancel}
        />
      </div>
    )
  }

  const renderPrimaryField = attendee => (
    <StackView axis="horizontal" spacing={1}>
      <div className="card__primary">{attendee.name}</div>
      <Button
        as="button"
        icon={{ name: "general.newWindow" }}
        tooltip={{ title: "View Registration" }}
        variant="naked"
        size="sm"
        onClick={e => handleViewRegistration(attendee, e)}
      />
      {statusRowMessage && statusRowMessage.id === attendee.id && (
        <StatusMessage {...statusRowMessage} style={{ marginLeft: "0.5rem", paddingTop: 1 }} />
      )}
    </StackView>
  )

  const renderAreaMobile = attendee => {
    const isLast = attendee.id == rows[rows.length - 1].id
    const areaItems = attendee => {
      return sortBy("isDisabled", getAreaItems(attendee), "desc")
    }
    return (
      <Dropdown
        title={attendee.assignedArea?.name || "Select an area"}
        size="xs"
        placement={isLast ? "top-start" : "bottom-start"}
        keepInView={true}
        closeOnSelect={true}
      >
        {attendee.assignedArea && (
          <Dropdown.Item onSelect={() => handleMobileAttendeeSave(attendee, 0)}>
            <span className="c-error">Unassign</span>
          </Dropdown.Item>
        )}
        {areaItems(attendee).map(option => {
          if (option.id === attendee.assignedArea?.id) {
            return
          } else {
            return (
              <Dropdown.Item
                key={option.id}
                disabled={option.isDisabled}
                onSelect={() => handleMobileAttendeeSave(attendee, option.id)}
              >
                {option.render(option)}
              </Dropdown.Item>
            )
          }
        })}
      </Dropdown>
    )
  }

  const handleMobileAttendeeSave = (attendee, areaId) => {
    setStatusRowMessage({ id: attendee.id, text: "Saving...", icon: "spinner" })
    const area = areas[areaId]

    if (areaId === 0 && attendee.assignedArea) {
      onAttendeeUnassign([attendee], areas[attendee.assignedArea.id])
        .then(_resp => setSuccessStatus(attendee.id))
        .catch(_error => setErrorStatus(attendee.id))
    } else if ((area && !attendee.assignedArea) || (area && attendee.assignedArea.id !== area.id)) {
      confirmableAssignmentUpdate({
        area,
        attendee,
        onConfirm: () => {
          onAttendeeAssign(attendee, area)
            .then(_resp => setSuccessStatus(attendee.id))
            .catch(error => setErrorStatus(attendee.id, error.message))
        },
        onCancel: () => {
          setStatusRowMessage(null)
        }
      })
    }

    setTimeout(() => setStatusRowMessage(null), 2500)
  }

  const renderActionMenuItems = () => {
    const items = [
      {
        text: "Unassign all",
        onClick: () => handleUnassignAll(attendees),
        className: "ta-l"
      },
      {
        text: "Auto assign",
        onClick: handleAutoAssign,
        className: "ta-l"
      }
    ]
    if (!event.isSimple) {
      items.push({
        text: !subscribedToCheckIns && "Check in",
        render: subscribedToCheckIns ? renderCheckInButton : renderLegacyCheckInButton,
        className: !subscribedToCheckIns && "ta-l"
      })
    }
    return items
  }

  const handleAssignmentCsvExport = assigmentExportPath => () => {
    api
      .post(assigmentExportPath)
      .then(_ => addToast("You will receive an email when your export is ready"))
  }

  const renderHeading = () => {
    return (
      <>
        {statusMessage && <StatusMessage {...statusMessage} style={{ marginRight: "1rem" }} />}

        {attendeeType === "all" && (
          <>
            <DropdownMenu
              className="btn-outline mr-1"
              text="Actions"
              items={renderActionMenuItems()}
            />
            <DropdownMenu
              className="btn-outline"
              text="Reports"
              icon="registrations#reports"
              items={[
                {
                  text: "Assignments by area",
                  href: assignmentTypePaths.assignmentsByArea,
                  isExternal: true
                },
                {
                  text: "Assignments by attendee",
                  href: assignmentTypePaths.assignmentsByAttendee,
                  isExternal: true
                },
                {
                  text: "Export CSV",
                  onClick: handleAssignmentCsvExport(assignmentTypePaths.assignmentsCsvExport)
                }
              ]}
            />
          </>
        )}
        {attendeeType === "assigned" && (
          <Button
            theme="destroy"
            onClick={() => handleUnassignAll(attendees)}
            disabled={!!statusMessage || attendees.length === 0}
            title="Unassign all"
          />
        )}
        {attendeeType === "unassigned" && (
          <Button
            theme="create"
            onClick={handleAutoAssign}
            disabled={!!statusMessage || attendees.length === 0}
            title="Auto assign"
          />
        )}
      </>
    )
  }

  const renderViewRegistrationButton = (row, _col) => {
    return (
      <Button
        as="button"
        icon={{ name: "general.newWindow" }}
        tooltip={{ title: "View Registration" }}
        variant="naked"
        onClick={e => handleViewRegistration(row, e)}
      />
    )
  }

  const columns = [
    {
      cell: "firstName",
      label: "First Name",
      isSortable: true,
      isShown: true
    },
    {
      cell: "lastName",
      label: "Last Name",
      isSortable: true,
      isShown: true
    },
    {
      cell: "type",
      label: "Selection",
      isSortable: true,
      className: "table__truncated-cell",
      isShown: true
    },
    {
      cell: "signupTime.contextualDateTime",
      label: "For Date",
      isSortable: true,
      isShown: signupFeatures.includes("separate_times")
    },
    {
      cell: "assignedArea.name",
      label: "Area",
      render: renderAreaColumn,
      className: "py-1",
      isSortable: true,
      isShown: true
    },
    { cell: "actions", label: "", render: renderViewRegistrationButton }
  ]

  const rows = sortBy(sorter.sortBy, attendees, sorter.sortDir)

  return (
    <DynamicDataView
      heading={{
        headerText: `${capitalize(attendeeType)} attendees`,
        headerSubText: pluralize(
          attendeeType === "all" ? "attendee" : attendeeType,
          attendeeType === "all" ? "attendees" : attendeeType,
          attendees.length
        )
      }}
      headingChildren={renderHeading()}
      overflowWrapperStyle={{ "border-radius": "0" }}
      style={{ "border-radius": "0" }}
      columns={columns.filter(c => c.isShown)}
      rows={rows}
      onRowClick={handleRowClick}
      onCardClick={null}
      defaultSort="firstName"
      onSortClick={handleSort}
      primaryField={renderPrimaryField}
      secondaryFields={["type", item => item.signupTime?.contextualDateTime || ""]}
      footerField={renderAreaMobile}
    />
  )
}

export function toSentence(arrayOfStrings) {
  const length = arrayOfStrings.length
  function getOperator({ index, length }) {
    if (index !== length - 1) return ", "
    if (index === 1) return " and "
    return ", and "
  }
  return arrayOfStrings.reduce((acc, string, index) => {
    if (acc) return [acc, string].join(getOperator({ index, length }))
    return string
  }, "")
}

Attendees.propTypes = {
  assignmentTypeId: PropTypes.string,
  attendeeType: PropTypes.oneOf(["all", "assigned", "unassigned"]),
  assignmentTypes: PropTypes.array,
  apiBaseUrl: PropTypes.string,
  checkInsBaseUrl: PropTypes.string,
  checkinsUrl: PropTypes.string,
  assignmentTypePaths: PropTypes.shape({
    assignmentsByArea: PropTypes.string,
    assignmentsByAttendee: PropTypes.string,
    assignmentsCsvExport: PropTypes.string,
    checkinsCreate: PropTypes.string
  }),
  areas: PropTypes.shape({}),
  assignedAttendees: PropTypes.array,
  unassignedAttendees: PropTypes.array,
  onAttendeeAssign: PropTypes.func,
  onAttendeeUnassign: PropTypes.func,
  onUnassignAllAlert: PropTypes.func,
  onAutoAssign: PropTypes.func,
  event: PropTypes.shape({
    id: PropTypes.string,
    signupIntegration: PropTypes.shape({
      integration_id: PropTypes.string,
      id: PropTypes.string
    }),
    isSimple: PropTypes.bool
  }),
  signupFeatures: PropTypes.array,
  subscribedToCheckIns: PropTypes.bool,
  checkInsSettingsPath: PropTypes.string
}

export default Attendees
